import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  AppBar,
  Toolbar,
  Avatar,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add,
  Delete,
  Search,
  Receipt,
  Print,
  Save,
  Cancel,
  ExpandMore,
  Payment,
  AttachMoney,
  CreditCard,
  PhoneAndroid,
  Inventory,
  AccountBalance,
  AccountBalanceWallet,
  Schedule,
  CardGiftcard,
  MoreHoriz,
  CheckCircle,
  Warning,
  Done,
  Cake,
  Close,
} from '@mui/icons-material';
import { keyframes } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { getCustomers, createCustomer } from '../redux/actions/customerActions';
import { getProducts, createProduct } from '../redux/actions/productActions';
import { getActivePaymentModes } from '../redux/actions/paymentModeActions';
import { getClosedChitsForCustomer } from '../redux/actions/chitActions';
import { getTaxes } from '../redux/actions/taxActions';
import { getCustomerOrders } from '../redux/actions/goldsmithActions';
// Import invoice actions directly
import * as invoiceActions from '../redux/actions/invoiceActions';
import { fetchCurrentFYInvoiceNumberMasters, generateNextInvoiceNumber, getLastInvoiceNumber, peekNextInvoiceNumber } from '../redux/actions/invoiceNumberMasterActions';

// Animation keyframes
const tickAnimation = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const scaleIn = keyframes`
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Billing = () => {
  const dispatch = useDispatch();
  const { customers } = useSelector((state) => state.customers);
  const { products } = useSelector((state) => state.products);
  const { activePaymentModes } = useSelector((state) => state.paymentModes);
  const { closedChits, closedChitsLoading } = useSelector((state) => state.chits);
  const { taxes } = useSelector((state) => state.taxes);
  const { currentInvoice, loading } = useSelector((state) => state.invoices);
  const { currentFYMaster, currentFinancialYear } = useSelector((state) => state.invoiceNumberMaster);
  const { customerOrders, ordersLoading } = useSelector((state) => state.goldsmith);

  const [customer, setCustomer] = useState(null);
  const [openCustomerSearchDialog, setOpenCustomerSearchDialog] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [category, setCategory] = useState(''); // Gold, Silver, Diamond
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]); // Invoice date
  const [items, setItems] = useState([]);
  const [creatingInvoice, setCreatingInvoice] = useState(false); // Local loading state for invoice creation
  const [searchProduct, setSearchProduct] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [weight, setWeight] = useState('');
  const [rate, setRate] = useState('');
  const [description, setDescription] = useState('');
  const [huid, setHuid] = useState('');
  const [discount, setDiscount] = useState(0);
  const [paymentModes, setPaymentModes] = useState([]); // Array of payment mode objects
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [deleteItemDialog, setDeleteItemDialog] = useState({ open: false, index: null });
  const [selectedChits, setSelectedChits] = useState([]); // Array of selected closed chits
  const [selectedOrders, setSelectedOrders] = useState([]); // Array of selected customer orders
  const [orderReference, setOrderReference] = useState(''); // Order reference input
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState(''); // Next invoice number for selected category
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [openNewProductDialog, setOpenNewProductDialog] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    gstin: '',
    date_of_birth: ''
  });
  const [newProduct, setNewProduct] = useState({
    name: '',
    item_code: '',
    category: '',
    weight: '',
    price_per_gram: '',
    hsn_code: '',
    huid: '',
    stock_qty: ''
  });
  
  // Old metal exchange states
  const [oldMetals, setOldMetals] = useState([]);
  const [newOldMetal, setNewOldMetal] = useState({
    description: '',
    weight: '',
    rate: '',
    amount: 0
  });

  // Notification state for success messages (toast)
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info' // 'success', 'info'
  });

  // Error Dialog state
  const [errorDialog, setErrorDialog] = useState({
    open: false,
    title: 'Error',
    message: ''
  });

  // Success Dialog state
  const [successDialog, setSuccessDialog] = useState({
    open: false,
    title: 'Success',
    message: '',
    invoiceNo: '',
    invoiceDate: '',
    amount: '',
    invoiceId: null
  });

  // Notification helper functions
  const showNotification = (message, severity = 'info') => {
    // Show errors in error dialog
    if (severity === 'error') {
      showErrorDialog(message);
      return;
    }
    // Show success in success dialog
    if (severity === 'success') {
      showSuccessDialog(message);
      return;
    }
    // Show info messages in toast (for non-critical messages)
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  // Error Dialog helper functions
  const showErrorDialog = (message, title = 'Error') => {
    setErrorDialog({
      open: true,
      title,
      message
    });
  };

  const handleCloseErrorDialog = () => {
    setErrorDialog({
      open: false,
      title: 'Error',
      message: ''
    });
  };

  // Success Dialog helper functions
  const showSuccessDialog = (message, title = 'Success', invoiceData = null) => {
    setSuccessDialog({
      open: true,
      title,
      message,
      invoiceNo: invoiceData?.invoice_no || '',
      invoiceDate: invoiceData?.invoice_date || '',
      amount: invoiceData?.grand_total || '',
      invoiceId: invoiceData?.id || null
    });
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialog({
      open: false,
      title: 'Success',
      message: '',
      invoiceNo: '',
      invoiceDate: '',
      amount: '',
      invoiceId: null
    });
  };

  const handlePrintInvoiceFromDialog = async () => {
    if (successDialog.invoiceId) {
      try {
        await dispatch(invoiceActions.generateBhavaniJewelleryPDF(successDialog.invoiceId));
        // Close the success dialog after initiating print
        handleCloseSuccessDialog();
      } catch (error) {
        showErrorDialog('Failed to generate PDF. Please try again.');
      }
    }
  };

  const handleCancelInvoice = () => {
    // Reset all form data
    setCustomer(null);
    setItems([]);
    setPaymentModes([]);
    setOldMetals([]);
    setSelectedOrders([]);
    setOrderReference('');
    setInvoiceDate(new Date().toISOString().split('T')[0]);
    setCategory('Gold');
    setDiscount(0);
    setSelectedChits([]);
    setNewOldMetal({
      metal_type: '',
      weight: '',
      rate: '',
      amount: ''
    });
    
    // Show success notification
    setNotification({
      open: true,
      message: 'Invoice cancelled and form reset',
      severity: 'info'
    });
  };

  // Category options
  const categoryOptions = [
    { value: 'Gold', label: 'Gold', hsnCode: '7113' },
    { value: 'Silver', label: 'Silver', hsnCode: '7114' },
    { value: 'Diamond', label: 'Diamond', hsnCode: '7101' },
  ];

  // Payment mode icons mapping
  const paymentIcons = {
    'AttachMoney': <AttachMoney />,
    'CreditCard': <CreditCard />,
    'PhoneAndroid': <PhoneAndroid />,
    'AccountBalance': <AccountBalance />,
    'Receipt': <Receipt />,
    'AccountBalanceWallet': <AccountBalanceWallet />,
    'Schedule': <Schedule />,
    'CardGiftcard': <CardGiftcard />,
    'MoreHoriz': <MoreHoriz />,
  };

  useEffect(() => {
    // Don't load all customers on mount - use search dialog instead
    dispatch(getProducts({ page: 1, limit: 1000 }));
    dispatch(getActivePaymentModes());
    dispatch(getTaxes({ page: 1, limit: 1000 }));
    dispatch(fetchCurrentFYInvoiceNumberMasters());
  }, [dispatch]);

  // Search customers with debounce
  useEffect(() => {
    if (!openCustomerSearchDialog) return;

    const timeoutId = setTimeout(async () => {
      setCustomerSearchLoading(true);
      try {
        await dispatch(getCustomers({ 
          page: 1, 
          limit: 50, 
          search: customerSearchTerm.trim() || undefined 
        }));
      } catch (error) {
        // Handle error
      } finally {
        setCustomerSearchLoading(false);
      }
    }, 300); // Debounce 300ms

    return () => clearTimeout(timeoutId);
  }, [customerSearchTerm, openCustomerSearchDialog, dispatch]);

  // Use customers from Redux store for search results
  useEffect(() => {
    if (openCustomerSearchDialog) {
      setCustomerSearchResults(customers || []);
    }
  }, [customers, openCustomerSearchDialog]);

  // Filter products based on selected category
  const filteredProducts = products.filter(product => {
    if (!category) return true;
    return product.category === category;
  });

  // Fetch closed chits when customer is selected
  useEffect(() => {
    if (customer?.id) {
      dispatch(getClosedChitsForCustomer(customer.id));
    } else {
      setSelectedChits([]);
    }
  }, [customer, dispatch]);

  // Fetch customer orders when customer is selected
  useEffect(() => {
    if (customer?.id) {
      dispatch(getCustomerOrders({ customer_id: customer.id, status: 'completed' }));
    } else {
      setSelectedOrders([]);
    }
  }, [customer, dispatch]);

  // Fetch next invoice number when category changes
  useEffect(() => {
    if (category) {
      fetchNextInvoiceNumber(category);
    } else {
      setNextInvoiceNumber('');
    }
  }, [category]);

  const calculateItemTotal = (item) => {
    const baseAmount = (item.weight || 0) * (item.rate || 0);
    
    // Get GST percentage from tax master based on HSN code
    let gstPercent = 3; // Default GST
    if (item.product && item.product.hsn_code) {
      const taxInfo = taxes.find(tax => tax.hsn_code === item.product.hsn_code);
      if (taxInfo) {
        gstPercent = parseFloat(taxInfo.gst_percent);
      }
    }
    
    const gstAmount = baseAmount * (gstPercent / 100);
    return Math.round(baseAmount + gstAmount); // Round off to nearest whole number
  };

  const calculateOldMetalTotal = () => {
    return oldMetals.reduce((sum, metal) => sum + (metal.amount || 0), 0);
  };

  const calculateChitTotal = () => {
    return selectedChits.reduce((sum, chit) => sum + (chit.closure_amount || 0), 0);
  };

  const calculateOrderTotal = () => {
    return selectedOrders.reduce((sum, order) => sum + (parseFloat(order.estimated_amount) || 0), 0);
  };

  const calculateTotals = () => {
    // Calculate subtotal as sum of amounts WITHOUT tax
    // If final_amount is set, use the calculated amount (without tax)
    // Otherwise, use weight * rate
    let subtotal = 0;
    
    items.forEach(item => {
      if (item.final_amount && item.final_amount > 0 && item.amount) {
        // Use the calculated amount (without tax) from final_amount
        subtotal += item.amount;
      } else {
        // Use weight * rate (amount without tax)
        const baseAmount = (item.weight || 0) * (item.rate || 0);
        subtotal += baseAmount;
      }
    });

    // Apply discount to subtotal first
    const discountAmount = subtotal * (discount / 100);
    const subtotalAfterDiscount = subtotal - discountAmount;

    const oldMetalTotal = calculateOldMetalTotal();
    const chitTotal = calculateChitTotal();
    const orderTotal = calculateOrderTotal();
    const subtotalAfterDeductions = subtotalAfterDiscount - oldMetalTotal - chitTotal - orderTotal;
    
    // Calculate GST on subtotal after deductions
    // Use 3% GST (or calculate based on items if needed)
    let gstPercent = 3; // Default GST
    if (items.length > 0 && items[0].product && items[0].product.hsn_code) {
      const taxInfo = taxes.find(tax => tax.hsn_code === items[0].product.hsn_code);
      if (taxInfo) {
        gstPercent = parseFloat(taxInfo.gst_percent);
      }
    }
    
    const gstAmount = subtotalAfterDeductions * (gstPercent / 100);
    const grandTotal = subtotalAfterDeductions + gstAmount;

    return {
      subtotal: Math.round(subtotal),
      discountAmount: Math.round(discountAmount),
      subtotalAfterDiscount: Math.round(subtotalAfterDiscount),
      oldMetalTotal: Math.round(oldMetalTotal),
      chitTotal: Math.round(chitTotal),
      orderTotal: Math.round(orderTotal),
      subtotalAfterDeductions: Math.round(subtotalAfterDeductions),
      gstAmount: Math.round(gstAmount),
      grandTotal: Math.round(grandTotal),
    };
  };

  const getPaymentIcon = (iconName) => {
    return paymentIcons[iconName] || <Payment />;
  };

  const handlePaymentModeToggle = (mode) => {
    setPaymentModes(prev => {
      const existing = prev.find(p => p.modeId === mode.id);
      if (existing) {
        return prev.filter(p => p.modeId !== mode.id);
      } else {
        return [...prev, {
          modeId: mode.id,
          modeCode: mode.code,
          modeName: mode.name,
          amount: '',
          reference: '',
          bankDetails: '',
          requiresReference: mode.requires_reference,
          requiresBank: mode.requires_bank,
        }];
      }
    });
  };

  const handlePaymentAmountChange = (modeId, amount) => {
    setPaymentModes(prev => 
      prev.map(p => p.modeId === modeId ? { ...p, amount } : p)
    );
  };

  const handlePaymentReferenceChange = (modeId, reference) => {
    setPaymentModes(prev => 
      prev.map(p => p.modeId === modeId ? { ...p, reference } : p)
    );
  };

  const handlePaymentBankDetailsChange = (modeId, bankDetails) => {
    setPaymentModes(prev => 
      prev.map(p => p.modeId === modeId ? { ...p, bankDetails } : p)
    );
  };

  const calculatePaymentTotal = () => {
    return paymentModes.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  };

  const getRemainingAmount = () => {
    const totals = calculateTotals();
    const paymentTotal = calculatePaymentTotal();
    return parseFloat(totals.grandTotal) - paymentTotal;
  };

  const handleAddItem = () => {
    if (!selectedProduct || !quantity) return;

    const itemWeight = parseFloat(weight) || selectedProduct.weight || 0;
    const itemRate = parseFloat(rate) || selectedProduct.price_per_gram || 0;
    const baseAmount = itemWeight * itemRate; // Amount without tax

    const newItem = {
      product_id: selectedProduct.id,
      product: selectedProduct,
      qty: parseInt(quantity),
      weight: itemWeight,
      rate: itemRate,
      description: description || selectedProduct.name,
      huid: huid || selectedProduct.huid || '',
      gst_percent: 3,
      amount: baseAmount, // Amount without tax
      total_amount: calculateItemTotal({
        weight: itemWeight,
        rate: itemRate,
      }),
    };

    setItems([...items, newItem]);
    setSelectedProduct(null);
    setQuantity(1);
    setWeight('');
    setRate('');
    setDescription('');
    setHuid('');
    setSearchProduct('');
    setOpenProductDialog(false);
  };

  const handleRemoveItem = (index) => {
    setDeleteItemDialog({ open: true, index });
  };

  const handleConfirmDeleteItem = () => {
    if (deleteItemDialog.index !== null) {
      setItems(items.filter((_, i) => i !== deleteItemDialog.index));
      setDeleteItemDialog({ open: false, index: null });
    }
  };

  const handleCancelDeleteItem = () => {
    setDeleteItemDialog({ open: false, index: null });
  };

  const handleFinalAmountChange = (index, value) => {
    setItems(items.map((item, i) => {
      if (i === index) {
        const finalAmount = value ? parseFloat(value) : null;
        let amount = null; // Amount without tax
        
        if (finalAmount && finalAmount > 0) {
          // Get GST percentage for this item
          let gstPercent = 3; // Default GST
          if (item.product && item.product.hsn_code) {
            const taxInfo = taxes.find(tax => tax.hsn_code === item.product.hsn_code);
            if (taxInfo) {
              gstPercent = parseFloat(taxInfo.gst_percent);
            }
          }
          
          // Calculate amount without tax from final amount (which includes tax)
          // If final amount is 100 and GST is 3%, then amount = 100 / (1 + 0.03) = 97.09
          amount = finalAmount / (1 + (gstPercent / 100));
        }
        
        return { 
          ...item, 
          final_amount: finalAmount,
          amount: amount // Store amount without tax
        };
      }
      return item;
    }));
  };

  const handleAddOldMetal = () => {
    if (!newOldMetal.description || !newOldMetal.weight || !newOldMetal.rate) {
      showNotification('Please fill in all old metal details', 'error');
      return;
    }

    // Use manually entered amount if provided, otherwise calculate from weight * rate
    const amount = newOldMetal.amount && parseFloat(newOldMetal.amount) > 0 
      ? parseFloat(newOldMetal.amount) 
      : parseFloat(newOldMetal.weight) * parseFloat(newOldMetal.rate);
    
    const oldMetal = {
      ...newOldMetal,
      amount: Math.round(amount),
      weight: parseFloat(newOldMetal.weight),
      rate: parseFloat(newOldMetal.rate)
    };

    setOldMetals([...oldMetals, oldMetal]);
    setNewOldMetal({
      description: '',
      weight: '',
      rate: '',
      amount: 0
    });
  };

  const handleRemoveOldMetal = (index) => {
    setOldMetals(oldMetals.filter((_, i) => i !== index));
  };

  const handleOldMetalChange = (field, value) => {
    setNewOldMetal(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      // Auto-calculate amount if weight and rate are provided and amount is not manually set
      if (field !== 'amount' && updated.weight && updated.rate && (!updated.amount || updated.amount === 0 || updated.amount === '')) {
        const calculated = Math.round(parseFloat(updated.weight || 0) * parseFloat(updated.rate || 0));
        updated.amount = calculated || '';
      }
      return updated;
    });
  };

  const handleChitToggle = (chit) => {
    setSelectedChits(prev => {
      const existing = prev.find(c => c.id === chit.id);
      if (existing) {
        return prev.filter(c => c.id !== chit.id);
      } else {
        return [...prev, chit];
      }
    });
  };

  const handleOrderToggle = (order) => {
    setSelectedOrders(prev => {
      const existing = prev.find(o => o.id === order.id);
      if (existing) {
        return prev.filter(o => o.id !== order.id);
      } else {
        return [...prev, order];
      }
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  // Function to capitalize first letter of each word
  const toTitleCase = (str) => {
    if (!str) return '';
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const handleCreateCustomer = async () => {
    // Only name is required
    if (!newCustomer.name || !newCustomer.name.trim()) {
      showNotification('Please fill in customer name', 'error');
      return;
    }
    // Validate email format if provided
    if (newCustomer.email && newCustomer.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newCustomer.email)) {
      showNotification('Invalid email format', 'error');
      return;
    }
    // Validate GSTIN format if provided
    if (newCustomer.gstin && newCustomer.gstin.trim() && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(newCustomer.gstin)) {
      showNotification('Invalid GSTIN format (e.g., 12ABCDE1234F1Z5)', 'error');
      return;
    }

    try {
      // Clean up empty strings - convert to null for optional fields
      const customerData = {
        name: newCustomer.name.trim(),
        phone: newCustomer.phone?.trim() || null,
        email: newCustomer.email?.trim() || null,
        address: newCustomer.address?.trim() || null,
        gstin: newCustomer.gstin?.trim() || null,
        date_of_birth: newCustomer.date_of_birth?.trim() || null
      };
      
      const result = await dispatch(createCustomer(customerData));
      if (result.success) {
        setCustomer(result.data);
        setNewCustomer({
          name: '',
          phone: '',
          email: '',
          address: '',
          gstin: '',
          date_of_birth: ''
        });
        setOpenCustomerDialog(false);
        dispatch(getCustomers({ page: 1, limit: 1000 })); // Refresh customer list
      }
    } catch (error) {
      showErrorDialog(error.message || 'Failed to create customer. Please try again.');
    }
  };

  const handleCreateProduct = async () => {
    if (!newProduct.name || !newProduct.item_code || !newProduct.category) {
      showNotification('Please fill in product name, item code, and category', 'error');
      return;
    }

    try {
      const result = await dispatch(createProduct(newProduct));
      if (result.success) {
        setSelectedProduct(result.data);
        setNewProduct({
          name: '',
          item_code: '',
          category: '',
          weight: '',
          price_per_gram: '',
          hsn_code: '',
          huid: '',
          stock_qty: ''
        });
        setOpenNewProductDialog(false);
        dispatch(getProducts({ page: 1, limit: 1000 })); // Refresh product list
      }
    } catch (error) {
      showErrorDialog(error.message || 'Failed to create product. Please try again.');
    }
  };

  // Generate next invoice number based on category
  const generateNextInvoiceNumberLocal = async (category) => {
    try {
      const result = await dispatch(generateNextInvoiceNumber(category || 'GENERAL'));
      return result.data.invoice_number;
    } catch (error) {
      // Silently fallback for invoice number generation
      // Fallback to simple generation
      const today = new Date();
      const year = today.getFullYear();
      const shortYear = year.toString().slice(-2);
      const nextYear = (year + 1).toString().slice(-2);
      const yearRange = `${shortYear}-${nextYear}`;
      
      let prefix = 'INV';
      if (category) {
        switch (category.toLowerCase()) {
          case 'gold': prefix = 'G'; break;
          case 'silver': prefix = 'S'; break;
          case 'diamond': prefix = 'D'; break;
          default: prefix = 'INV';
        }
      }
      
      return `${prefix}/${yearRange}/000001`;
    }
  };

  // Fetch next invoice number for display (non-incrementing)
  const fetchNextInvoiceNumber = async (category) => {
    if (!category) {
      setNextInvoiceNumber('');
      return;
    }
    
    try {
      const result = await dispatch(peekNextInvoiceNumber(category));
      setNextInvoiceNumber(result.data?.invoice_number);
    } catch (error) {
      // Silently handle error for fetching next invoice number
      setNextInvoiceNumber('');
    }
  };

  const handleCreateInvoice = async () => {
    if (items.length === 0) {
      showNotification('Please add at least one item', 'error');
      return;
    }

    if (!category) {
      showNotification('Please select a category for invoice numbering', 'error');
      return;
    }

    const remainingAmount = getRemainingAmount();
    if (Math.abs(remainingAmount) > 0.01) {
      showNotification(`Payment total (₹${Math.round(calculatePaymentTotal())}) does not match invoice total (₹${calculateTotals().grandTotal}). Remaining: ₹${Math.round(remainingAmount)}`, 'error');
      return;
    }

    // Validate payment modes - require reference for non-cash payments
    for (const payment of paymentModes) {
      if (payment.amount && parseFloat(payment.amount) > 0) {
        const paymentMode = activePaymentModes.find(m => m.id === payment.modeId);
        if (paymentMode && paymentMode.code !== 'CASH' && !payment.reference) {
          showErrorDialog(`${paymentMode.name} requires a transaction reference number. Please enter the reference number before creating the invoice.`);
          return;
        }
      }
    }

    // Don't generate invoice number here - let backend handle it to avoid double increment
    const invoiceData = {
      customer_id: customer?.id || null,
      category: category, // Add category for backend invoice numbering
      invoice_date: invoiceDate, // Use selected invoice date
      items: items.map(item => {
        // Calculate amount without tax
        let amount = item.amount; // Use pre-calculated amount if available
        if (!amount) {
          if (item.final_amount && item.final_amount > 0) {
            // Calculate from final_amount
            let gstPercent = 3;
            if (item.product && item.product.hsn_code) {
              const taxInfo = taxes.find(tax => tax.hsn_code === item.product.hsn_code);
              if (taxInfo) {
                gstPercent = parseFloat(taxInfo.gst_percent);
              }
            }
            amount = item.final_amount / (1 + (gstPercent / 100));
          } else {
            // Calculate from weight * rate
            amount = (item.weight || 0) * (item.rate || 0);
          }
        }
        
        return {
          product_id: item.product_id,
          qty: item.qty,
          weight: item.weight,
          rate: item.rate,
          description: item.description,
          huid: item.huid || '',
          hsnCode: item.product.hsn_code || '7113',
          amount: amount, // Amount without tax
          final_amount: item.final_amount || null,
        };
      }),
      old_metals: oldMetals.map(metal => ({
        description: metal.description,
        weight: metal.weight,
        rate: metal.rate,
        amount: metal.amount,
      })),
      selected_chits: selectedChits.map(chit => ({
        chit_id: chit.id,
        chit_number: chit.chit_number,
        closure_amount: chit.closure_amount,
        closure_type: chit.closure_type
      })),
      selected_orders: selectedOrders.map(order => ({
        order_id: order.id,
        order_number: order.order_number,
        order_amount: parseFloat(order.estimated_amount),
        order_date: order.order_date
      })),
      order_reference: orderReference,
      discount: parseFloat(discount),
      payment_modes: paymentModes.map(p => ({
        mode_code: p.modeCode,
        amount: parseFloat(p.amount),
        reference: p.reference,
        bank_details: p.bankDetails,
      })),
    };

    try {
      setCreatingInvoice(true);
      const result = await dispatch(invoiceActions.createInvoice(invoiceData));
      if (result.success) {
        // Show success dialog with invoice details
        showSuccessDialog(
          'Invoice created successfully!',
          'Invoice Created',
          result.data
        );
        // Reset form
        setCustomer(null);
        setCategory('');
        setInvoiceDate(new Date().toISOString().split('T')[0]);
        setItems([]);
        setDiscount(0);
        setPaymentModes([]);
        setOldMetals([]);
        setSelectedChits([]);
        setSelectedOrders([]);
        setOrderReference('');
      } else {
        // Handle API error response
        showErrorDialog(result.message || 'Failed to create invoice. Please try again.');
      }
    } catch (error) {
      // Handle network or unexpected errors
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create invoice. Please try again.';
      // Check if error response has multiple errors
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const errorList = error.response.data.errors.map(e => `• ${e.message || e}`).join('\n');
        showErrorDialog(`Please fix the following errors:\n\n${errorList}`);
      } else {
        showErrorDialog(errorMessage);
      }
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handlePrintInvoice = async () => {
    if (currentInvoice) {
      await dispatch(invoiceActions.generateBhavaniJewelleryPDF(currentInvoice.id));
    }
  };

  const totals = calculateTotals();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', pb: 4 }}>
      {/* Modern Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 3,
          mb: 3,
          borderRadius: '0 0 16px 16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
              <Receipt sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Create Invoice
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Generate a new invoice for your customer
              </Typography>
            </Box>
          </Box>
          {nextInvoiceNumber && category && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mb: 0.5 }}>
                Next Invoice Number
              </Typography>
              <Chip 
                label={nextInvoiceNumber} 
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white', 
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  height: 32
                }} 
              />
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ maxWidth: '1600px', mx: 'auto', px: { xs: 2, md: 3 } }}>
      <Grid container spacing={3}>
        {/* Customer Selection - Modern Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                  <PhoneAndroid />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  Customer Details
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Customer"
                  value={customer ? customer.name : ''}
                  placeholder="Click to search customer..."
                  size="small"
                  onFocus={(e) => {
                    e.target.blur();
                    if (!openCustomerSearchDialog) {
                      setOpenCustomerSearchDialog(true);
                      setCustomerSearchTerm('');
                    }
                  }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!openCustomerSearchDialog) {
                              setOpenCustomerSearchDialog(true);
                              setCustomerSearchTerm('');
                            }
                          }}
                          edge="end"
                        >
                          <Search color="action" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    flexGrow: 1,
                    cursor: 'pointer',
                    '& .MuiInputBase-input': {
                      cursor: 'pointer'
                    }
                  }}
                />
                <Tooltip title="Add New Customer">
                  <IconButton
                    color="primary"
                    onClick={() => setOpenCustomerDialog(true)}
                    sx={{ 
                      border: '2px dashed',
                      borderColor: 'primary.main',
                      '&:hover': { bgcolor: 'primary.light', color: 'white' }
                    }}
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
                {customer && (
                  <Tooltip title="Change Customer">
                    <IconButton
                      color="secondary"
                      onClick={() => {
                        setCustomer(null);
                        setOpenCustomerSearchDialog(true);
                      }}
                      size="small"
                    >
                      <Cancel fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {customer && (
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  bgcolor: 'primary.50', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'primary.100',
                  position: 'relative'
                }}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setCustomer(null);
                      setOpenCustomerSearchDialog(true);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      color: 'text.secondary',
                      '&:hover': {
                        bgcolor: 'primary.100'
                      }
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary.dark">
                      {toTitleCase(customer.name)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Grid container spacing={1}>
                    {customer.address && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Address</Typography>
                        <Typography variant="body2" fontWeight="medium">{customer.address}</Typography>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Phone</Typography>
                      <Typography variant="body2" fontWeight="medium">{customer.phone || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Typography variant="body2" fontWeight="medium">{customer.email || 'N/A'}</Typography>
                    </Grid>
                    {customer.gstin && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">GSTIN</Typography>
                        <Typography variant="body2" fontWeight="medium">{customer.gstin}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Category & Date Selection - Modern Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                  <Inventory />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  Invoice Settings
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Item Category</InputLabel>
                  <Select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    label="Item Category"
                  >
                    <MenuItem value="">
                      <em>Select Category</em>
                    </MenuItem>
                    {categoryOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: option.value === 'Gold' ? '#FFD700' :
                                      option.value === 'Silver' ? '#C0C0C0' : '#B9F2FF'
                            }}
                          />
                          {option.label} ({option.hsnCode})
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  label="Invoice Date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                {category && (
                  <Box sx={{ 
                    mt: 1, 
                    p: 2, 
                    bgcolor: 'success.50', 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'success.100'
                  }}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Category Info
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight="medium">Products:</Typography>
                      <Chip label={filteredProducts.length} size="small" color="success" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" fontWeight="medium">HSN:</Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {categoryOptions.find(opt => opt.value === category)?.hsnCode}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Order Reference - Modern Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'info.main', width: 40, height: 40 }}>
                  <Receipt />
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  Additional Info
                </Typography>
              </Box>
              <TextField
                fullWidth
                value={orderReference}
                onChange={(e) => setOrderReference(e.target.value)}
                label="Order Reference"
                placeholder="Enter order reference (optional)"
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Receipt color="action" /></InputAdornment>
                }}
                sx={{ mb: 2 }}
              />
              {totals.grandTotal > 0 && (
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'warning.50', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'warning.100'
                }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Quick Total
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="warning.dark">
                    ₹{totals.grandTotal.toLocaleString('en-IN')}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Closed Chits Section - Modern */}
        {customer && closedChits && closedChits.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                    <CardGiftcard />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Available Closed Chits
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Select closed chits to apply as credit against this invoice
                    </Typography>
                  </Box>
                </Box>
                
                <TableContainer 
                  component={Paper} 
                  sx={{ 
                    borderRadius: 2,
                    boxShadow: 'none',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Select</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Chit Number</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Group</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Closure Type</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Closure Date</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {closedChits.map((chit) => {
                        const isSelected = selectedChits.some(c => c.id === chit.id);
                        return (
                          <TableRow key={chit.id}>
                            <TableCell>
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleChitToggle(chit)}
                                color="primary"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {chit.chit_number}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {chit.chitGroup?.group_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatCurrency(chit.chitGroup?.chit_amount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={chit.closure_type?.replace('_', ' ').toUpperCase()}
                                size="small"
                                color={chit.closure_type === 'completed' ? 'success' : 'warning'}
                              />
                            </TableCell>
                            <TableCell>
                              {chit.closure_date ? new Date(chit.closure_date).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {formatCurrency(chit.closure_amount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={chit.status.replace('_', ' ').toUpperCase()}
                                size="small"
                                color={chit.status === 'completed' ? 'success' : 'warning'}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Selected Chits Summary - Modern */}
                {selectedChits.length > 0 && (
                  <Box sx={{ 
                    mt: 3, 
                    p: 2.5, 
                    bgcolor: 'success.50', 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'success.100'
                  }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="success.dark">
                      Selected Chits Summary
                    </Typography>
                    {selectedChits.map((chit) => (
                      <Box key={chit.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {chit.chit_number} - {chit.chitGroup?.group_name}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(chit.closure_amount)}
                        </Typography>
                      </Box>
                    ))}
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" fontWeight="bold" color="success.dark">
                        Total Chit Credit:
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="success.dark">
                        -₹{Math.round(calculateChitTotal()).toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Customer Orders Section - Modern */}
        {customer && customerOrders && customerOrders.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'info.main', width: 40, height: 40 }}>
                    <Schedule />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Available Customer Orders
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Select completed orders to apply as credit against this invoice
                    </Typography>
                  </Box>
                </Box>
                
                <TableContainer 
                  component={Paper} 
                  sx={{ 
                    borderRadius: 2,
                    boxShadow: 'none',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Select</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Order Number</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Weight</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Touch</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Order Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {customerOrders.map((order) => {
                        const isSelected = selectedOrders.some(o => o.id === order.id);
                        return (
                          <TableRow key={order.id}>
                            <TableCell>
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleOrderToggle(order)}
                                color="primary"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {order.order_number}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {order.design_details || 'Custom Order'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {order.product_description}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {order.estimated_weight} g
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {order.estimated_touch}%
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {formatCurrency(order.estimated_amount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {order.order_date ? new Date(order.order_date).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={order.status.replace('_', ' ').toUpperCase()}
                                size="small"
                                color={order.status === 'completed' ? 'success' : 'warning'}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Selected Orders Summary - Modern */}
                {selectedOrders.length > 0 && (
                  <Box sx={{ 
                    mt: 3, 
                    p: 2.5, 
                    bgcolor: 'info.50', 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'info.100'
                  }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="info.dark">
                      Selected Orders Summary
                    </Typography>
                    {selectedOrders.map((order) => (
                      <Box key={order.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {order.order_number} - {order.design_details || 'Custom Order'}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(order.estimated_amount)}
                        </Typography>
                      </Box>
                    ))}
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" fontWeight="bold" color="info.dark">
                        Total Order Credit:
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="info.dark">
                        -₹{Math.round(calculateOrderTotal()).toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Items Section - Modern Card */}
        <Grid item xs={12}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main', width: 40, height: 40 }}>
                    <Inventory />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Invoice Items
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Add products to the invoice
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Create New Product">
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={() => setOpenNewProductDialog(true)}
                      size="small"
                    >
                      New Product
                    </Button>
                  </Tooltip>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {
                      if (!category) {
                        showErrorDialog('Please select a category before adding items to the invoice.');
                        return;
                      }
                      setOpenProductDialog(true);
                    }}
                    size="small"
                    sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5568d3 0%, #653d91 100%)',
                      }
                    }}
                  >
                    Add Item
                  </Button>
                </Box>
              </Box>

              {items.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 6,
                  border: '2px dashed',
                  borderColor: 'grey.300',
                  borderRadius: 2,
                  bgcolor: 'grey.50'
                }}>
                  <Inventory sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No items added yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Click "Add Item" to start building your invoice
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {
                      if (!category) {
                        showErrorDialog('Please select a category before adding items to the invoice.');
                        return;
                      }
                      setOpenProductDialog(true);
                    }}
                  >
                    Add First Item
                  </Button>
                </Box>
              ) : (
                <TableContainer 
                  component={Paper} 
                  sx={{ 
                    borderRadius: 2,
                    boxShadow: 'none',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>HUID</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Weight (g)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rate</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>GST%</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Final Amount</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item, index) => (
                        <TableRow 
                          key={index}
                          sx={{ 
                            '&:hover': { bgcolor: 'action.hover' },
                            '&:last-child td': { border: 0 }
                          }}
                        >
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {item.product.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.product.item_code} • {item.product.category}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 200 }}>
                              {item.description}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={item.huid || 'N/A'} 
                              size="small" 
                              variant="outlined"
                              sx={{ maxWidth: 120 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={item.qty} size="small" color="primary" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              {parseFloat(item.weight).toFixed(3)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              ₹{parseFloat(item.rate).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">{item.gst_percent}%</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              ₹{Math.round(item.amount || (item.weight || 0) * (item.rate || 0)).toLocaleString('en-IN')}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={item.final_amount || ''}
                              onChange={(e) => handleFinalAmountChange(index, e.target.value)}
                              placeholder="Final"
                              InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                inputProps: { min: 0, step: 0.01 },
                                sx: { width: 130 }
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Remove Item">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveItem(index)}
                                color="error"
                                sx={{ 
                                  '&:hover': { 
                                    bgcolor: 'error.light',
                                    color: 'white'
                                  }
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Old Metal Exchange - Modern Card */}
        <Grid item xs={12}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
                  <Inventory />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Old Metal Exchange
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Add old metal items for exchange credit
                  </Typography>
                </Box>
              </Box>
              
              {/* Add Old Metal Form - Modern */}
              <Box sx={{ 
                mb: 3, 
                p: 3, 
                bgcolor: 'warning.50', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'warning.100'
              }}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={newOldMetal.description}
                      onChange={(e) => handleOldMetalChange('description', e.target.value)}
                      placeholder="e.g., Gold Chain, Silver Ring"
                      size="small"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="Weight (g)"
                      type="number"
                      value={newOldMetal.weight}
                      onChange={(e) => handleOldMetalChange('weight', e.target.value)}
                      inputProps={{ step: "0.001", min: 0 }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="Rate per Gram"
                      type="number"
                      value={newOldMetal.rate}
                      onChange={(e) => handleOldMetalChange('rate', e.target.value)}
                      inputProps={{ step: "0.01", min: 0 }}
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="Amount"
                      type="number"
                      value={newOldMetal.amount || ''}
                      onChange={(e) => handleOldMetalChange('amount', e.target.value)}
                      InputProps={{ 
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>
                      }}
                      size="small"
                      sx={{ bgcolor: 'white' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button
                      variant="contained"
                      onClick={handleAddOldMetal}
                      disabled={!newOldMetal.description || !newOldMetal.weight || !newOldMetal.rate}
                      fullWidth
                      startIcon={<Add />}
                      sx={{
                        bgcolor: 'warning.main',
                        '&:hover': { bgcolor: 'warning.dark' },
                        height: '40px'
                      }}
                    >
                      Add Old Metal
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {/* Old Metal List - Modern */}
              {oldMetals.length > 0 && (
                <>
                  <TableContainer 
                    component={Paper} 
                    sx={{ 
                      mb: 2,
                      borderRadius: 2,
                      boxShadow: 'none',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Weight (g)</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rate</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {oldMetals.map((metal, index) => (
                          <TableRow 
                            key={index}
                            sx={{ 
                              '&:hover': { bgcolor: 'action.hover' },
                              '&:last-child td': { border: 0 }
                            }}
                          >
                            <TableCell>{metal.description}</TableCell>
                            <TableCell align="right">{parseFloat(metal.weight).toFixed(3)}</TableCell>
                            <TableCell align="right">₹{parseFloat(metal.rate).toFixed(2)}</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold" color="warning.dark">
                                ₹{Math.round(metal.amount).toLocaleString('en-IN')}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Remove">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveOldMetal(index)}
                                  color="error"
                                  sx={{ 
                                    '&:hover': { 
                                      bgcolor: 'error.light',
                                      color: 'white'
                                    }
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Old Metal Summary - Modern */}
                  <Box sx={{ 
                    p: 2.5, 
                    bgcolor: 'warning.100', 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'warning.200',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="warning.dark">
                      Total Old Metal Value:
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="warning.dark">
                      -₹{Math.round(calculateOldMetalTotal()).toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Details - Modern Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                  <Payment />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Payment Details
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Select payment methods and amounts
                  </Typography>
                </Box>
              </Box>
              
              {/* Payment Modes Accordion - Modern */}
              <Box sx={{ mb: 2 }}>
                {activePaymentModes.map((mode) => {
                  const isSelected = paymentModes.some(p => p.modeId === mode.id);
                  const selectedPayment = paymentModes.find(p => p.modeId === mode.id);
                  
                  return (
                    <Accordion 
                      key={mode.id} 
                      expanded={isSelected}
                      onChange={() => handlePaymentModeToggle(mode)}
                      sx={{ 
                        mb: 1.5,
                        borderRadius: 2,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                        '&:before': { display: 'none' }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{ 
                          backgroundColor: isSelected ? 'primary.50' : 'grey.50',
                          borderRadius: 2,
                          minHeight: 56,
                          '&:hover': { backgroundColor: isSelected ? 'primary.100' : 'grey.100' },
                          '&.Mui-expanded': {
                            backgroundColor: 'primary.50',
                            borderRadius: '8px 8px 0 0'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handlePaymentModeToggle(mode)}
                            onClick={(e) => e.stopPropagation()}
                            color="primary"
                            size="small"
                          />
                          <Box sx={{ color: mode.color || 'primary.main', display: 'flex', alignItems: 'center' }}>
                            {getPaymentIcon(mode.icon)}
                          </Box>
                          <Typography variant="body1" fontWeight={isSelected ? 'bold' : 'medium'}>
                            {mode.name}
                          </Typography>
                          {isSelected && selectedPayment?.amount && (
                            <Chip 
                              label={`₹${parseFloat(selectedPayment.amount || 0).toLocaleString('en-IN')}`} 
                              size="small" 
                              color="primary" 
                              sx={{ ml: 'auto', fontWeight: 'bold' }}
                            />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ bgcolor: 'white', borderRadius: '0 0 8px 8px' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                          <TextField
                            fullWidth
                            label="Amount (₹)"
                            type="number"
                            value={selectedPayment?.amount || ''}
                            onChange={(e) => handlePaymentAmountChange(mode.id, e.target.value)}
                            placeholder="Enter amount"
                            size="small"
                            required
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            inputProps={{ step: "0.01", min: 0 }}
                          />
                          
                          {/* Show reference field for all non-cash payments */}
                          {mode.code !== 'CASH' && (
                            <TextField
                              fullWidth
                              label="Transaction Reference"
                              value={selectedPayment?.reference || ''}
                              onChange={(e) => handlePaymentReferenceChange(mode.id, e.target.value)}
                              placeholder="Enter transaction reference number"
                              size="small"
                              required
                              error={!selectedPayment?.reference && selectedPayment?.amount ? true : false}
                              helperText={!selectedPayment?.reference && selectedPayment?.amount ? 'Transaction reference is required for non-cash payments' : ''}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Receipt sx={{ fontSize: 18, color: 'text.secondary' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                          
                          {mode.requires_bank && (
                            <TextField
                              fullWidth
                              label="Bank Details"
                              value={selectedPayment?.bankDetails || ''}
                              onChange={(e) => handlePaymentBankDetailsChange(mode.id, e.target.value)}
                              multiline
                              rows={2}
                              placeholder="Enter bank name, account number, etc."
                              size="small"
                            />
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Invoice Summary - Modern Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%', 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
            borderRadius: 2,
            border: '2px solid',
            borderColor: totals.grandTotal > 0 ? 'primary.main' : 'divider'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                  <Receipt />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Invoice Summary
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Review totals and complete invoice
                  </Typography>
                </Box>
              </Box>
              
              {/* Discount Input - Modern */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Discount (%)"
                  type="number"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  inputProps={{ min: 0, max: 100 }}
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">%</InputAdornment>,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'grey.50',
                      '&:hover': { bgcolor: 'grey.100' }
                    }
                  }}
                />
              </Box>

              {/* Summary Breakdown - Modern */}
              <Box sx={{ 
                p: 2.5, 
                bgcolor: 'grey.50', 
                borderRadius: 2,
                mb: 3,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">Sub Total:</Typography>
                  <Typography variant="body2" fontWeight="bold">₹{totals.subtotal.toLocaleString('en-IN')}</Typography>
                </Box>
                
                {discount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="body2" color="error.main">
                      Discount ({discount}%):
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="error.main">
                      -₹{totals.discountAmount.toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                )}

                {oldMetals.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="body2" color="warning.dark">Old Metal Exchange:</Typography>
                    <Typography variant="body2" fontWeight="bold" color="warning.dark">
                      -₹{totals.oldMetalTotal.toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                )}

                {selectedChits.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="body2" color="success.dark">Chit Amount:</Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.dark">
                      -₹{totals.chitTotal.toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                )}

                {selectedOrders.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="body2" color="info.dark">Order Amount:</Typography>
                    <Typography variant="body2" fontWeight="bold" color="info.dark">
                      -₹{totals.orderTotal.toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2" fontWeight="medium">Taxable Value:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    ₹{totals.subtotalAfterDeductions.toLocaleString('en-IN')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" fontWeight="medium">GST (3%):</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    ₹{totals.gstAmount.toLocaleString('en-IN')}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1.5, borderWidth: 2 }} />

                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  p: 2,
                  bgcolor: 'primary.50',
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: 'primary.main'
                }}>
                  <Typography variant="h6" fontWeight="bold" color="primary.dark">
                    Grand Total:
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary.dark">
                    ₹{totals.grandTotal.toLocaleString('en-IN')}
                  </Typography>
                </Box>
              </Box>

              {/* Payment Validation - Modern */}
              {paymentModes.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {Math.abs(getRemainingAmount()) <= 0.01 ? (
                    <Alert 
                      severity="success" 
                      sx={{ 
                        borderRadius: 2,
                        '& .MuiAlert-icon': { alignItems: 'center' }
                      }}
                      icon={<CheckCircle />}
                    >
                      <Typography variant="body2" fontWeight="medium">
                        Payment amount matches invoice total
                      </Typography>
                    </Alert>
                  ) : (
                    <Alert 
                      severity="warning" 
                      sx={{ 
                        borderRadius: 2,
                        '& .MuiAlert-icon': { alignItems: 'center' }
                      }}
                    >
                      <Typography variant="body2" fontWeight="medium">
                        Payment mismatch: {getRemainingAmount() > 0 ? 'Add ₹' + Math.round(getRemainingAmount()).toLocaleString('en-IN') : 'Reduce ₹' + Math.round(Math.abs(getRemainingAmount())).toLocaleString('en-IN')}
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}

              {/* Payment Summary - Modern */}
              {paymentModes.length > 0 && (
                <Box sx={{ 
                  p: 2.5, 
                  bgcolor: 'success.50', 
                  borderRadius: 2, 
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'success.100'
                }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="success.dark">
                    Payment Summary
                  </Typography>
                  {paymentModes.map((payment) => (
                    <Box key={payment.modeId} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">{payment.modeName}:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        ₹{Math.round(parseFloat(payment.amount || 0)).toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight="bold">Total Paid:</Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.dark" fontSize="1.1rem">
                      ₹{Math.round(calculatePaymentTotal()).toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" fontWeight="bold">
                      {getRemainingAmount() > 0 ? 'Remaining:' : 'Overpaid:'}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight="bold" 
                      color={getRemainingAmount() > 0 ? 'error.main' : 'success.main'}
                      fontSize="1.1rem"
                    >
                      {getRemainingAmount() > 0 ? '+' : ''}₹{Math.round(Math.abs(getRemainingAmount())).toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Action Buttons - Modern */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={handleCancelInvoice}
                  disabled={creatingInvoice}
                  sx={{ 
                    minWidth: 120,
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={creatingInvoice ? <CircularProgress size={20} color="inherit" /> : <Save />}
                  onClick={handleCreateInvoice}
                  disabled={creatingInvoice || items.length === 0 || (paymentModes.length > 0 && Math.abs(getRemainingAmount()) > 0.01)}
                  sx={{ 
                    flex: 1,
                    borderRadius: 2,
                    textTransform: 'none',
                    py: 1.5,
                    background: items.length > 0 ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : undefined,
                    '&:hover': items.length > 0 ? {
                      background: 'linear-gradient(135deg, #5568d3 0%, #653d91 100%)',
                    } : undefined,
                    '&:disabled': {
                      background: 'grey.300'
                    }
                  }}
                >
                  {creatingInvoice ? 'Creating...' : 'Create Invoice'}
                </Button>
                {currentInvoice && (
                  <Button
                    variant="outlined"
                    startIcon={<Print />}
                    onClick={handlePrintInvoice}
                    fullWidth
                    sx={{ 
                      borderRadius: 2,
                      textTransform: 'none'
                    }}
                  >
                    Print Invoice
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Summary card removed; content moved inside Invoice Summary */}
      </Grid>
      </Box>

      {/* Product Selection Dialog - Modern */}
      <Dialog 
        open={openProductDialog} 
        onClose={() => setOpenProductDialog(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'secondary.main',
          color: 'white',
          pb: 2.5,
          pt: 3,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                <Search sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Select Product
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Choose a product to add to the invoice
                </Typography>
              </Box>
            </Box>
            {category && (
              <Chip 
                label={category}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  fontWeight: 'bold',
                  height: 32
                }}
              />
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
          {/* Search Bar - Modern */}
          <Box sx={{ mb: 3, mt: 1 }}>
            <TextField
              fullWidth
              label="Search Products"
              value={searchProduct}
              onChange={(e) => setSearchProduct(e.target.value)}
              placeholder="Search by name, item code, or category..."
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: searchProduct && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchProduct('')}
                      edge="end"
                    >
                      <Cancel sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  '&:hover': { bgcolor: 'grey.100' },
                  '&.Mui-focused': { bgcolor: 'white' }
                }
              }}
            />
          </Box>

          {/* Products Table - Modern */}
          {filteredProducts.filter(product => 
            product.name.toLowerCase().includes(searchProduct.toLowerCase()) ||
            product.item_code.toLowerCase().includes(searchProduct.toLowerCase())
          ).length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 8,
              border: '2px dashed',
              borderColor: 'grey.300',
              borderRadius: 2,
              bgcolor: 'grey.50'
            }}>
              <Inventory sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No products found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchProduct ? 'Try adjusting your search terms' : 'No products available for this category'}
              </Typography>
            </Box>
          ) : (
            <TableContainer 
              component={Paper} 
              sx={{ 
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'divider',
                maxHeight: '50vh',
                '& .MuiTableContainer-root': {
                  overflowX: 'auto'
                }
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Item Code</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Product Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }} align="center">Category</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }} align="right">Weight (g)</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }} align="right">Price/Gram</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }} align="center">Stock</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }} align="center">HUID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProducts
                    .filter(product => 
                      product.name.toLowerCase().includes(searchProduct.toLowerCase()) ||
                      product.item_code.toLowerCase().includes(searchProduct.toLowerCase())
                    )
                    .map((product) => (
                      <TableRow 
                        key={product.id}
                        sx={{ 
                          '&:hover': { 
                            bgcolor: 'action.hover',
                            cursor: 'pointer'
                          },
                          '&:last-child td': { border: 0 }
                        }}
                        onClick={() => setSelectedProduct(product)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium" fontFamily="monospace">
                            {product.item_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {toTitleCase(product.name)}
                            </Typography>
                            {product.hsn_code && (
                              <Typography variant="caption" color="text.secondary">
                                HSN: {product.hsn_code}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={product.category} 
                            size="small" 
                            color={
                              product.category === 'Gold' ? 'warning' :
                              product.category === 'Silver' ? 'default' : 'info'
                            }
                            sx={{ 
                              fontWeight: 'medium',
                              minWidth: 80
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {product.weight ? parseFloat(product.weight).toFixed(3) : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="success.dark">
                            {product.price_per_gram ? `₹${parseFloat(product.price_per_gram).toFixed(2)}` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={product.stock_qty || 0} 
                            size="small" 
                            color={!product.stock_qty || product.stock_qty <= 5 ? 'error' : 'success'}
                            sx={{ 
                              fontWeight: 'bold',
                              minWidth: 60
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {product.huid ? (
                            <Chip 
                              label={product.huid} 
                              size="small" 
                              variant="outlined"
                              sx={{ 
                                fontSize: '0.7rem',
                                maxWidth: 100,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProduct(product);
                            }}
                            variant="contained"
                            color="primary"
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              px: 2,
                              boxShadow: 'none',
                              '&:hover': {
                                boxShadow: 2,
                                transform: 'translateY(-1px)'
                              }
                            }}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Products Count */}
          {filteredProducts.filter(product => 
            product.name.toLowerCase().includes(searchProduct.toLowerCase()) ||
            product.item_code.toLowerCase().includes(searchProduct.toLowerCase())
          ).length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Showing {filteredProducts.filter(product => 
                  product.name.toLowerCase().includes(searchProduct.toLowerCase()) ||
                  product.item_code.toLowerCase().includes(searchProduct.toLowerCase())
                ).length} product(s)
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Add />}
                onClick={() => setOpenNewProductDialog(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none'
                }}
              >
                Add New Product
              </Button>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
          <Button 
            onClick={() => setOpenProductDialog(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              minWidth: 100
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Item Details Dialog - Modern */}
      {selectedProduct && (
        <Dialog 
          open={!!selectedProduct} 
          onClose={() => setSelectedProduct(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          }}
        >
          <DialogTitle sx={{ 
            bgcolor: 'primary.main',
            color: 'white',
            pb: 2.5,
            pt: 3,
            px: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                <Inventory sx={{ fontSize: 28 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Add Item Details
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Enter item information for the invoice
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          
          {/* Product Info Card */}
          <Box sx={{ px: 3, pt: 3, pb: 0 }}>
            <Card sx={{ 
              bgcolor: 'primary.50', 
              border: '1px solid',
              borderColor: 'primary.100',
              borderRadius: 2,
              p: 2.5,
              mb: 3
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  <Inventory sx={{ fontSize: 32 }} />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.dark" gutterBottom>
                    {toTitleCase(selectedProduct.name)}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    <Chip 
                      label={selectedProduct.item_code} 
                      size="small" 
                      variant="outlined"
                      sx={{ borderColor: 'primary.main', color: 'primary.dark' }}
                    />
                    <Chip 
                      label={selectedProduct.category} 
                      size="small" 
                      color="primary"
                      variant="outlined"
                    />
                    {selectedProduct.weight && (
                      <Chip 
                        label={`${parseFloat(selectedProduct.weight).toFixed(3)}g`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {selectedProduct.price_per_gram && (
                      <Chip 
                        label={`₹${parseFloat(selectedProduct.price_per_gram).toFixed(2)}/g`}
                        size="small"
                        variant="outlined"
                        color="success"
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Card>
          </Box>

          <DialogContent sx={{ px: 3, pb: 2 }}>
            <Grid container spacing={3}>
              {/* Quantity and Weight Row */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  inputProps={{ min: 1 }}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ color: 'text.secondary' }}>#</Box>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'grey.50',
                      '&:hover': { bgcolor: 'grey.100' },
                      '&.Mui-focused': { bgcolor: 'white' }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Weight (grams)"
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder={selectedProduct.weight ? parseFloat(selectedProduct.weight).toFixed(3) : 'Enter weight'}
                  inputProps={{ step: "0.001", min: 0 }}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ color: 'text.secondary' }}>g</Box>
                      </InputAdornment>
                    ),
                  }}
                  helperText={selectedProduct.weight && `Default: ${parseFloat(selectedProduct.weight).toFixed(3)}g`}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'grey.50',
                      '&:hover': { bgcolor: 'grey.100' },
                      '&.Mui-focused': { bgcolor: 'white' }
                    }
                  }}
                />
              </Grid>

              {/* Rate per Gram */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Rate per Gram"
                  type="number"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  placeholder={selectedProduct.price_per_gram ? parseFloat(selectedProduct.price_per_gram).toFixed(2) : 'Enter rate'}
                  inputProps={{ step: "0.01", min: 0 }}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoney sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  helperText={selectedProduct.price_per_gram && `Default: ₹${parseFloat(selectedProduct.price_per_gram).toFixed(2)}/g`}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'grey.50',
                      '&:hover': { bgcolor: 'grey.100' },
                      '&.Mui-focused': { bgcolor: 'white' }
                    }
                  }}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter custom description (optional)"
                  multiline
                  rows={3}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                        <Receipt sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'grey.50',
                      '&:hover': { bgcolor: 'grey.100' },
                      '&.Mui-focused': { bgcolor: 'white' }
                    }
                  }}
                />
              </Grid>

              {/* HUID */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="HUID"
                  value={huid}
                  onChange={(e) => setHuid(e.target.value)}
                  placeholder={selectedProduct.huid || 'Enter HUID (optional)'}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Inventory sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  helperText={selectedProduct.huid && `Product HUID: ${selectedProduct.huid}`}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'grey.50',
                      '&:hover': { bgcolor: 'grey.100' },
                      '&.Mui-focused': { bgcolor: 'white' }
                    }
                  }}
                />
              </Grid>

              {/* Calculated Total Display */}
              {(weight && rate) && (
                <Grid item xs={12}>
                  <Card sx={{ 
                    bgcolor: 'success.50',
                    border: '2px solid',
                    borderColor: 'success.main',
                    borderRadius: 2,
                    p: 2.5,
                    boxShadow: '0 2px 8px rgba(46,125,50,0.1)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                          <AttachMoney sx={{ fontSize: 24 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Item Total (with GST)
                          </Typography>
                          <Typography variant="h5" fontWeight="bold" color="success.dark">
                            ₹{calculateItemTotal({
                              weight: parseFloat(weight) || 0,
                              rate: parseFloat(rate) || 0,
                            }).toLocaleString('en-IN')}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Base Amount
                        </Typography>
                        <Typography variant="body2" fontWeight="medium" color="text.secondary">
                          ₹{((parseFloat(weight) || 0) * (parseFloat(rate) || 0)).toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          + GST (3%)
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
            <Button 
              onClick={() => setSelectedProduct(null)}
              variant="outlined"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                minWidth: 100
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddItem} 
              variant="contained"
              disabled={!quantity || !weight || !rate}
              startIcon={<Add />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                minWidth: 140,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #653d91 100%)',
                },
                '&:disabled': {
                  background: 'grey.300'
                }
              }}
            >
              Add to Invoice
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Add New Customer Dialog */}
      <Dialog 
        open={openCustomerDialog} 
        onClose={() => setOpenCustomerDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <Box sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 3,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            zIndex: 0
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            zIndex: 0
          }} />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Add New Customer
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Create a new customer profile for billing and tracking
            </Typography>
          </Box>
        </Box>
        
        <DialogContent sx={{ p: 4, bgcolor: '#fafafa' }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Customer Name"
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                required
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: { fontWeight: 600 }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                variant="outlined"
                placeholder="+91 9876543210"
                helperText="Optional"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: { fontWeight: 600 }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={newCustomer.email}
                onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                variant="outlined"
                placeholder="customer@example.com"
                helperText="Optional"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: { fontWeight: 600 }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                value={newCustomer.date_of_birth || ''}
                onChange={(e) => setNewCustomer({ ...newCustomer, date_of_birth: e.target.value })}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                  sx: { fontWeight: 600 }
                }}
                helperText="Optional - Used for sending birthday wishes"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Cake color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={3}
                value={newCustomer.address}
                onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                variant="outlined"
                placeholder="Enter complete address with city, state, and pincode"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: { fontWeight: 600 }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="GSTIN (Optional)"
                value={newCustomer.gstin}
                onChange={(e) => setNewCustomer({ ...newCustomer, gstin: e.target.value })}
                variant="outlined"
                placeholder="22AAAAA0000A1Z5"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#667eea',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: { fontWeight: 600 }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, bgcolor: '#fafafa', gap: 2 }}>
          <Button 
            onClick={() => setOpenCustomerDialog(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              borderColor: '#ccc',
              color: '#666',
              '&:hover': {
                borderColor: '#999',
                bgcolor: '#f5f5f5'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateCustomer} 
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
              }
            }}
          >
            Create Customer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Product Dialog */}
      <Dialog 
        open={openNewProductDialog} 
        onClose={() => setOpenNewProductDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <Box sx={{ 
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          p: 3,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{
            position: 'absolute',
            top: -40,
            right: -40,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            zIndex: 0
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: -20,
            left: -20,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            zIndex: 0
          }} />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Add New Product
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Create a new product for your inventory and billing
            </Typography>
          </Box>
        </Box>
        
        <DialogContent sx={{ p: 4, bgcolor: '#fafafa' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Product Name"
                value={newProduct.name}
                onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                required
                variant="outlined"
                placeholder="e.g., Gold Chain, Silver Ring"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5576c',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5576c',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: { fontWeight: 600 }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Item Code"
                value={newProduct.item_code}
                onChange={(e) => setNewProduct({ ...newProduct, item_code: e.target.value })}
                required
                variant="outlined"
                placeholder="e.g., GC001, SR002"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5576c',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5576c',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: { fontWeight: 600 }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5576c',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5576c',
                    },
                  },
                }}
              >
                <InputLabel sx={{ fontWeight: 600 }}>Category</InputLabel>
                <Select
                  value={newProduct.category}
                  onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
                  label="Category"
                >
                  {categoryOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: option.value === 'Gold' ? '#FFD700' : 
                                  option.value === 'Silver' ? '#C0C0C0' : '#B9F2FF'
                        }} />
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="HSN Code"
                value={newProduct.hsn_code}
                onChange={(e) => setNewProduct({ ...newProduct, hsn_code: e.target.value })}
                placeholder={categoryOptions.find(opt => opt.value === newProduct.category)?.hsnCode || "e.g., 7113"}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5576c',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5576c',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: { fontWeight: 600 }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="HUID"
                value={newProduct.huid}
                onChange={(e) => setNewProduct({ ...newProduct, huid: e.target.value })}
                variant="outlined"
                placeholder="Enter HUID"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5576c',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5576c',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: { fontWeight: 600 }
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Inventory /></InputAdornment>
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Weight (grams)"
                type="number"
                value={newProduct.weight}
                onChange={(e) => setNewProduct({ ...newProduct, weight: e.target.value })}
                inputProps={{ step: "0.001", min: 0 }}
                variant="outlined"
                placeholder="0.000"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5576c',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5576c',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: { fontWeight: 600 }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Price per Gram (₹)"
                type="number"
                value={newProduct.price_per_gram}
                onChange={(e) => setNewProduct({ ...newProduct, price_per_gram: e.target.value })}
                inputProps={{ step: "0.01", min: 0 }}
                variant="outlined"
                placeholder="0.00"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5576c',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5576c',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: { fontWeight: 600 }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Initial Stock Quantity"
                type="number"
                value={newProduct.stock_qty}
                onChange={(e) => setNewProduct({ ...newProduct, stock_qty: e.target.value })}
                inputProps={{ min: 0 }}
                variant="outlined"
                placeholder="0"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5576c',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f5576c',
                    },
                  },
                }}
                InputLabelProps={{
                  sx: { fontWeight: 600 }
                }}
              />
            </Grid>
            
            {/* Product Preview Card */}
            {newProduct.name && newProduct.category && (
              <Grid item xs={12}>
                <Box sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: 'white',
                  border: '2px dashed #f5576c',
                  textAlign: 'center'
                }}>
                  <Typography variant="h6" color="primary" gutterBottom>
                    Product Preview
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 1 }}>
                    <Box sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: newProduct.category === 'Gold' ? '#FFD700' : 
                              newProduct.category === 'Silver' ? '#C0C0C0' : '#B9F2FF'
                    }} />
                    <Typography variant="body1" fontWeight="bold">
                      {newProduct.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {newProduct.item_code} • {newProduct.category}
                  </Typography>
                  {newProduct.weight && newProduct.price_per_gram && (
                    <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                      ₹{parseFloat(newProduct.weight * newProduct.price_per_gram).toFixed(2)} (approx. value)
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, bgcolor: '#fafafa', gap: 2 }}>
          <Button 
            onClick={() => setOpenNewProductDialog(false)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              borderColor: '#ccc',
              color: '#666',
              '&:hover': {
                borderColor: '#999',
                bgcolor: '#f5f5f5'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateProduct} 
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              boxShadow: '0 4px 15px rgba(245, 87, 108, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e083eb 0%, #e5475c 100%)',
                boxShadow: '0 6px 20px rgba(245, 87, 108, 0.6)',
              }
            }}
          >
            Create Product
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog */}
      <Dialog
        open={errorDialog.open}
        onClose={handleCloseErrorDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5,
          bgcolor: 'error.main',
          color: 'white',
          pb: 2,
          pt: 2.5
        }}>
          <Box sx={{ 
            bgcolor: 'rgba(255,255,255,0.2)', 
            borderRadius: '50%', 
            p: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Warning sx={{ fontSize: 28 }} />
          </Box>
          <Typography variant="h6" fontWeight="bold">
            {errorDialog.title}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body1" color="text.primary" sx={{ whiteSpace: 'pre-line' }}>
            {errorDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={handleCloseErrorDialog}
            variant="contained"
            color="error"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              py: 1,
              minWidth: 100
            }}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={successDialog.open}
        onClose={handleCloseSuccessDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5,
          bgcolor: 'success.main',
          color: 'white',
          pb: 2,
          pt: 2.5
        }}>
          <Box 
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)', 
              borderRadius: '50%', 
              p: 0.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: `${tickAnimation} 0.6s ease-in-out`
            }}
          >
            <CheckCircle 
              sx={{ 
                fontSize: 28,
                animation: `${scaleIn} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)`
              }} 
            />
          </Box>
          <Typography variant="h6" fontWeight="bold">
            {successDialog.title}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography 
            variant="body1" 
            color="text.primary" 
            sx={{ 
              whiteSpace: 'pre-line',
              mb: 3,
              textAlign: 'center',
              fontWeight: 500
            }}
          >
            {successDialog.message}
          </Typography>
          
          {/* Invoice Details */}
          {successDialog.invoiceNo && (
            <Box sx={{ 
              bgcolor: 'success.50', 
              borderRadius: 2, 
              p: 2.5,
              border: '1px solid',
              borderColor: 'success.200',
              mb: 2
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Bill Number */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="body2" color="text.secondary" fontWeight="medium">
                    Bill Number:
                  </Typography>
                  <Typography variant="body1" color="text.primary" fontWeight="bold">
                    {successDialog.invoiceNo}
                  </Typography>
                </Box>
                
                {/* Bill Date */}
                {successDialog.invoiceDate && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Bill Date:
                    </Typography>
                    <Typography variant="body1" color="text.primary" fontWeight="bold">
                      {new Date(successDialog.invoiceDate).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </Typography>
                  </Box>
                )}
                
                {/* Amount */}
                {successDialog.amount && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    py: 1,
                    bgcolor: 'success.100',
                    borderRadius: 1,
                    px: 1.5,
                    mt: 0.5
                  }}>
                    <Typography variant="body2" color="success.dark" fontWeight="bold">
                      Total Amount:
                    </Typography>
                    <Typography 
                      variant="h6" 
                      color="success.dark" 
                      fontWeight="bold"
                      sx={{
                        animation: `${fadeInUp} 0.5s ease-out 0.2s both`
                      }}
                    >
                      ₹{parseFloat(successDialog.amount).toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            onClick={handleCloseSuccessDialog}
            variant="outlined"
            color="success"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              py: 1,
              minWidth: 100
            }}
          >
            Close
          </Button>
          {successDialog.invoiceId && (
            <Button
              onClick={handlePrintInvoiceFromDialog}
              variant="contained"
              color="success"
              startIcon={<Print />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                py: 1,
                minWidth: 140,
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
                }
              }}
              autoFocus
            >
              Print Invoice
            </Button>
          )}
        </DialogActions>
      </Dialog>
      

      {/* Success/Info Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Delete Item Confirmation Dialog */}
      <Dialog
        open={deleteItemDialog.open}
        onClose={handleCancelDeleteItem}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'error.main',
          color: 'white',
          pb: 2.5,
          pt: 3,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
              <Delete sx={{ fontSize: 28 }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Delete Item
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Are you sure you want to delete this item?
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
          {deleteItemDialog.index !== null && items[deleteItemDialog.index] && (
            <Box sx={{ 
              bgcolor: 'grey.50',
              borderRadius: 2,
              p: 2.5,
              border: '1px solid',
              borderColor: 'grey.300'
            }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Item Details:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2">
                  <strong>Product:</strong> {items[deleteItemDialog.index].product?.name || items[deleteItemDialog.index].description}
                </Typography>
                <Typography variant="body2">
                  <strong>Quantity:</strong> {items[deleteItemDialog.index].qty}
                </Typography>
                <Typography variant="body2">
                  <strong>Weight:</strong> {parseFloat(items[deleteItemDialog.index].weight || 0).toFixed(3)}g
                </Typography>
                <Typography variant="body2">
                  <strong>Rate:</strong> ₹{parseFloat(items[deleteItemDialog.index].rate || 0).toFixed(2)}/g
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="error.main" sx={{ mt: 1 }}>
                  Amount: ₹{Math.round(parseFloat(items[deleteItemDialog.index].amount || 0)).toLocaleString('en-IN')}
                </Typography>
              </Box>
            </Box>
          )}
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
          <Button
            onClick={handleCancelDeleteItem}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              minWidth: 100
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteItem}
            variant="contained"
            color="error"
            startIcon={<Delete />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              minWidth: 120
            }}
          >
            Delete Item
          </Button>
        </DialogActions>
      </Dialog>

      {/* Customer Search Dialog - Modern */}
      <Dialog
        open={openCustomerSearchDialog}
        onClose={() => {
          setOpenCustomerSearchDialog(false);
          setCustomerSearchTerm('');
          setCustomerSearchResults([]);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main',
          color: 'white',
          pb: 2.5,
          pt: 3,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                <PhoneAndroid sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Select Customer
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Search and choose a customer for the invoice
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => {
                setOpenCustomerSearchDialog(false);
                setCustomerSearchTerm('');
                setCustomerSearchResults([]);
              }}
              size="small"
              sx={{ color: 'white' }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
          {/* Search Bar - Modern */}
          <Box sx={{ mb: 3, mt: 1 }}>
            <TextField
              fullWidth
              label="Search Customers"
              value={customerSearchTerm}
              onChange={(e) => setCustomerSearchTerm(e.target.value)}
              placeholder="Search by name, phone, or GSTIN..."
              size="small"
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: customerSearchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setCustomerSearchTerm('')}
                      edge="end"
                    >
                      <Cancel sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  '&:hover': { bgcolor: 'grey.100' },
                  '&.Mui-focused': { bgcolor: 'white' }
                }
              }}
            />
          </Box>

          {/* Customers List */}
          {customerSearchLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : customerSearchResults.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 8,
              border: '2px dashed',
              borderColor: 'grey.300',
              borderRadius: 2,
              bgcolor: 'grey.50'
            }}>
              <PhoneAndroid sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No customers found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {customerSearchTerm ? 'Try adjusting your search terms' : 'Start typing to search customers...'}
              </Typography>
            </Box>
          ) : (
            <TableContainer 
              component={Paper} 
              sx={{ 
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'divider',
                maxHeight: '50vh',
                '& .MuiTableContainer-root': {
                  overflowX: 'auto'
                }
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Customer Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>GSTIN</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 250 }}>Address</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customerSearchResults.map((cust) => (
                    <TableRow 
                      key={cust.id}
                      sx={{ 
                        '&:hover': { 
                          bgcolor: 'action.hover',
                          cursor: 'pointer'
                        },
                        '&:last-child td': { border: 0 },
                        bgcolor: customer?.id === cust.id ? 'primary.50' : 'transparent'
                      }}
                      onClick={() => {
                        setCustomer(cust);
                        setOpenCustomerSearchDialog(false);
                        setCustomerSearchTerm('');
                        setCustomerSearchResults([]);
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {toTitleCase(cust.name || '')}
                          </Typography>
                          {customer?.id === cust.id && (
                            <CheckCircle color="primary" sx={{ fontSize: 18 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {cust.phone || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {cust.gstin ? (
                          <Chip
                            label={cust.gstin}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 250 }}>
                          {cust.address || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCustomer(cust);
                            setOpenCustomerSearchDialog(false);
                            setCustomerSearchTerm('');
                            setCustomerSearchResults([]);
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
          <Button
            onClick={() => {
              setOpenCustomerSearchDialog(false);
              setOpenCustomerDialog(true);
            }}
            startIcon={<Add />}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3
            }}
          >
            Add New Customer
          </Button>
          <Button
            onClick={() => {
              setOpenCustomerSearchDialog(false);
              setCustomerSearchTerm('');
              setCustomerSearchResults([]);
            }}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Billing;
